import { Component, OnInit, Input, OnChanges, SimpleChanges, OnDestroy, Output, EventEmitter, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// *** Import Chart.js v2 types and BaseChartDirective ***
import { ChartDataSets, ChartOptions, ChartPoint, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
// ----------------------------------------------------
import {
  PersistenceApiService,
  ClusterReportResponse,
  ClusterDefinition,
  ClusterItemInfo // This now includes x, y
} from '../../../../shared/services/persistence-api.service'; // Adjust path
import Swal from 'sweetalert2'; // For feedback

@Component({
  selector: 'app-cluster-report',
  templateUrl: './cluster-report.component.html',
  styleUrls: ['./cluster-report.component.scss']
})
export class ClusterReportComponent implements OnInit, OnChanges, OnDestroy {

// --- ADD CONSTANTS AS PUBLIC READONLY PROPERTIES HERE ---
  public readonly CLUSTER_COLORS = [
    'rgba(255, 99, 132, 0.7)',  // Red
    'rgba(54, 162, 235, 0.7)', // Blue
    'rgba(255, 206, 86, 0.7)', // Yellow
    'rgba(75, 192, 192, 0.7)', // Teal
    'rgba(153, 102, 255, 0.7)',// Purple
    'rgba(255, 159, 64, 0.7)', // Orange
    'rgba(100, 100, 100, 0.7)',// Dark Grey
    'rgba(144, 238, 144, 0.7)',// Light Green
  ];
  public readonly NOISE_COLOR = 'rgba(201, 203, 207, 0.5)'; // Light Grey for noise
  // ---------------------------------------------------------

  // --- Inputs & Outputs (Keep as before) ---
  @Input() projectName: string | null = null;
  @Input() filterByFileId: string | null | undefined = null;
  @Input() filterFileName: string | null | undefined = null;
  @Output() clusterHovered = new EventEmitter<number | null>();
  @Output() itemClicked = new EventEmitter<string>();

  // --- State (Keep as before) ---
  isLoading = false;
  error: string | null = null;
  clusterReport: ClusterReportResponse | null = null;

  // --- NEW: State for Configuration Console ---
  // Initialize with default values matching backend defaults or desired starting point
  paramMinClusterSize: number = 5;
  paramMinSamples: number | null = 5; // Use null if backend handles None, or number matching default
  // Add UMAP params if you want to control them too
  // paramUmapNeighbors: number = 15;
  // paramUmapMinDist: number = 0.1;

  // --- NEW: State for Chart ---
  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;
  public scatterChartOptions: ChartOptions = {};
  public scatterChartDatasets: ChartDataSets[] = []; // Use ChartDataSets for v2
  public scatterChartType: ChartType = 'scatter';
  public scatterChartLegend = true; // Show legend for clusters

  // --- RxJS Subject (Keep as before) ---
  private destroy$ = new Subject<void>();

  constructor(private kbService: PersistenceApiService) { }

  // --- Lifecycle Hooks (Keep as before, loadClusterData will now use params) ---
  ngOnInit(): void {
    console.log('ClusterReportComponent initialized.');
    this.setupChartOptions(); // Setup chart options on init
    if (this.projectName) {
      this.loadClusterData();
    } else {
      console.warn('ClusterReportComponent: Project name not available on init.');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    let needsReload = false;
    if (changes['projectName'] && !changes['projectName'].firstChange) {
      needsReload = true;
    }
    if (changes['filterByFileId']) {
      needsReload = true;
    }
    if (needsReload && this.projectName) {
      // Reset parameters to default when project/file changes? Optional.
      // this.resetParameters();
      this.loadClusterData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Data Loading ---
  loadClusterData(): void {
    if (!this.projectName) {
      this.error = 'Project name is required.';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.clusterReport = null; // Clear previous report
    this.scatterChartDatasets = []; // Clear previous chart data

    console.log(`ClusterReport: Loading data for Project: ${this.projectName}, File Filter ID: ${this.filterByFileId}`);
    console.log(`ClusterReport: Using Parameters - min_cluster_size: ${this.paramMinClusterSize}, min_samples: ${this.paramMinSamples}`);

    // *** Prepare parameters object for the service call ***
    const clusterParams = {
      min_cluster_size: this.paramMinClusterSize,
      min_samples: this.paramMinSamples // Pass null or number as defined
      // umap_n_neighbors: this.paramUmapNeighbors, // Add if controlling UMAP
      // umap_min_dist: this.paramUmapMinDist,
    };
    // ----------------------------------------------------

    this.kbService.getClusterReport(this.projectName, this.filterByFileId, clusterParams) // Pass params
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (report) => {
          this.clusterReport = report;
          if (!report || (!report.clusters.length && !report.noise_points.length)) {
            if (this.filterByFileId) {
              this.error = `No cluster data found for file "${this.filterFileName || 'selected file'}". Try adjusting parameters.`;
            } else {
              this.error = 'No cluster data found or generated for the project. Try adjusting parameters.';
            }
            this.scatterChartDatasets = []; // Ensure chart is empty
          } else {
            this.error = null; // Clear error if data is received
            // *** Generate the plot data ***
            this.createClusterPlot(report);
            // *****************************
          }
          console.log('Cluster report received:', report);
          this.isLoading = false; // Set loading false on success
        },
        error: (err) => {
          console.error('ClusterReport: Error loading cluster data:', err);
          if (this.filterByFileId) {
            this.error = `Failed to load cluster report for file "${this.filterFileName || 'selected file'}": ${err.message || err}`;
          } else {
            this.error = `Failed to load cluster report: ${err.message || err}`;
          }
          this.clusterReport = null;
          this.scatterChartDatasets = []; // Ensure chart is empty on error
          this.isLoading = false; // Set loading false on error
        }
      });
  }

  // --- NEW: Method to Apply Parameters ---
  /**
   * Called when the 'Apply Parameters' button is clicked.
   * Triggers a reload of the cluster data with the current parameter values.
   */
  applyParameters(): void {
    console.log('Applying new parameters and reloading cluster data...');
    // Optional: Add validation for parameters here if needed
    if (this.paramMinClusterSize < 2) {
      Swal.fire('Invalid Parameter', 'Minimum Cluster Size must be 2 or greater.', 'warning');
      return;
    }
    if (this.paramMinSamples !== null && this.paramMinSamples < 1) { // min_samples can be 1, but often >= 2 makes more sense
      Swal.fire('Invalid Parameter', 'Minimum Samples must be 1 or greater (or null).', 'warning');
      return;
    }
    this.loadClusterData(); // Reload data with new params
  }

  // --- NEW: Chart Setup and Plotting ---

  /**
   * Sets up the static options for the scatter chart.
   */
  setupChartOptions(): void {
    this.scatterChartOptions = {
      responsive: true,
      maintainAspectRatio: false, // Allow custom height/width
      title: {
        display: true,
        // Title is dynamic based on project/file, set in createClusterPlot or getReportTitle
        text: 'Cluster Visualization',
        fontSize: 14
      },
      legend: {
        display: true, // Show legend to identify clusters
        position: 'bottom',
        labels: {
          boxWidth: 12,
          fontSize: 10
        }
      },
      scales: {
        xAxes: [{
          type: 'linear',
          position: 'bottom',
          scaleLabel: { display: false }, // Hide axis labels for cleaner look
          ticks: { display: false },      // Hide axis ticks
          gridLines: { display: false, drawBorder: false } // Hide grid lines
        }],
        yAxes: [{
          type: 'linear',
          scaleLabel: { display: false },
          ticks: { display: false },
          gridLines: { display: false, drawBorder: false }
        }]
      },
      tooltips: {
        enabled: true,
        mode: 'nearest', // Show tooltip for the nearest point
        callbacks: {
          label: (tooltipItem, data) => {
            // Find the dataset and point corresponding to the tooltip
            // Use nullish coalescing for safety
            const datasetIndex = tooltipItem.datasetIndex ?? 0;
            const dataIndex = tooltipItem.index ?? 0;
            const dataset = data.datasets?.[datasetIndex];
            // Use 'as any' carefully, or preferably define an interface for your point data
            const pointData = dataset?.data?.[dataIndex] as ({ x: number; y: number; itemTitle?: string; itemId?: string } | ChartPoint | undefined);

            // Prepare coordinates string with formatting
            let coordsString = '';
            // Option 1: Use original point data if available (likely more reliable numbers)
            if (pointData && typeof (pointData as any).x === 'number' && typeof (pointData as any).y === 'number') {
              coordsString = `(${(pointData as any).x.toFixed(2)}, ${(pointData as any).y.toFixed(2)})`;
            }
            // Option 2: Fallback to tooltipItem labels, checking type first
            else {
              const xVal = tooltipItem.xLabel; // Can be string | number | undefined
              const yVal = tooltipItem.yLabel; // Can be string | number | undefined

              // Format x and y safely
              const xStr = typeof xVal === 'number' ? xVal.toFixed(2) : (xVal ?? '?'); // Use ?? for undefined/null
              const yStr = typeof yVal === 'number' ? yVal.toFixed(2) : (yVal ?? '?'); // Use ?? for undefined/null
              coordsString = `(${xStr}, ${yStr})`;
            }


            // --- Construct the final tooltip label ---

            // If we have custom item title data, prioritize that
            if (pointData && (pointData as any).itemTitle) {
              let label = [`Title: ${(pointData as any).itemTitle}`];
              if (dataset?.label) {
                label.push(`Cluster: ${dataset.label}`);
              }
              label.push(`Coords: ${coordsString}`); // Add the formatted coordinates
              return label; // Return array for multi-line tooltips
            }

            // --- Fallback if no custom data found ---
            // Use the dataset label and formatted coordinates
            return `${dataset?.label || 'Point'} ${coordsString}`;
          }
        }
      },
      // Optional: Add onClick handler if needed later
      onClick: (event?: MouseEvent, activeElements?: any[]) => {
        if (activeElements && activeElements.length > 0) {
          const firstElement = activeElements[0];
          const datasetIndex = firstElement._datasetIndex; // Index of the dataset (cluster/noise)
          const index = firstElement._index; // Index of the point within that dataset
          const dataset = this.scatterChartDatasets[datasetIndex];
          const pointData = dataset?.data?.[index] as any; // Cast to access custom data

          if (pointData?.itemId) {
            console.log(`Chart point clicked: Item ID ${pointData.itemId}, Title: ${pointData.itemTitle}, Cluster Label: ${dataset.label}`);
            // Emit the item ID if an item point was clicked
            this.itemClicked.emit(pointData.itemId);
          } else {
            console.log(`Chart background or non-item element clicked.`);
          }
        }
      }
    };
  }

  /**
   * Processes the cluster report data and generates datasets for the scatter plot.
   * @param report The ClusterReportResponse containing cluster and noise data with coordinates.
   */
  createClusterPlot(report: ClusterReportResponse): void {
    const datasets: ChartDataSets[] = [];

    // 1. Process Clusters
    report.clusters.forEach((cluster, index) => {
      const colorIndex = index % this.CLUSTER_COLORS.length; // Cycle through colors
      const clusterColor = this.CLUSTER_COLORS[colorIndex];

      datasets.push({
        data: cluster.items.map(item => ({
          x: item.x,
          y: item.y,
          // *** Store extra info for tooltips/clicks ***
          itemTitle: item.title,
          itemId: item.id
          // ******************************************
        })),
        label: `Cluster ${cluster.cluster_id} (${cluster.size})`, // Legend label
        pointBackgroundColor: clusterColor,
        pointBorderColor: clusterColor.replace('0.7', '1'), // Make border solid
        pointRadius: 5,
        pointHoverRadius: 7,
        type: 'scatter', // Ensure type is scatter if mixing chart types (though not here)
      });
    });

    // 2. Process Noise Points
    if (report.noise_points.length > 0) {
      datasets.push({
        data: report.noise_points.map(item => ({
          x: item.x,
          y: item.y,
          // *** Store extra info for tooltips/clicks ***
          itemTitle: item.title,
          itemId: item.id
          // ******************************************
        })),
        label: `Noise (${report.noise_points.length})`,
        pointBackgroundColor: this.NOISE_COLOR,
        pointBorderColor: this.NOISE_COLOR.replace('0.5', '0.8'),
        pointRadius: 4, // Slightly smaller for noise
        pointHoverRadius: 6,
        type: 'scatter',
      });
    }

    // Update the chart title dynamically
    if (this.scatterChartOptions?.title) {
      this.scatterChartOptions.title.text = this.getReportTitle() + ` - Visualization`;
    }

    // Assign the generated datasets
    this.scatterChartDatasets = datasets;

    // Force chart update (especially needed with Chart.js v2/ng2-charts)
    setTimeout(() => {
      this.chart?.update();
    }, 0);

    console.log(`Generated ${datasets.length} datasets for cluster plot.`);
  }

  // --- Interaction Handlers (Keep as before) ---
  onClusterMouseEnter(clusterId: number): void {
    console.log('Hovering cluster list item:', clusterId);
    this.clusterHovered.emit(clusterId);
    // Optional: Add chart highlighting logic here later
  }

  onClusterMouseLeave(): void {
    this.clusterHovered.emit(null);
    // Optional: Remove chart highlighting logic here later
  }

  onItemClick(item: ClusterItemInfo): void {
    console.log('Clicked item list item:', item.id, item.title);
    this.itemClicked.emit(item.id);
  }

  // --- Title Helper (Keep as before) ---
  getReportTitle(): string {
    if (this.filterByFileId && this.filterFileName) {
      return `Cluster Analysis for File: ${this.filterFileName}`;
    } else if (this.projectName) {
      return `Cluster Analysis for Project: ${this.projectName}`;
    } else {
      return 'Cluster Analysis';
    }
  }
}
